import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, 
  BarChart3, 
  Copy, 
  Loader2, 
  CalendarRange, 
  DollarSign, 
  AlertCircle, 
  CheckCircle,
  FileText,
  Download,
  FileSpreadsheet,
  Edit3,
  Save,
  X
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { createPageUrl } from "@/utils";
// import { getAuthToken } from "@/auth"; // Removido - não utilizado
// Import jsPDF corretamente como módulo
import { jsPDF } from "jspdf";
// Importar autotable explicitamente
import 'jspdf-autotable';

export default function AnnualRevenue() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [revenue, setRevenue] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isCnpjEditing, setIsCnpjEditing] = useState(false);
  const [lastFormattedCnpj, setLastFormattedCnpj] = useState(""); // Armazena o último CNPJ formatado
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(null);
  const [copiedIndices, setCopiedIndices] = useState(new Set());
  const [lastCopiedIndexForAnimation, setLastCopiedIndexForAnimation] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [csvGenerating, setCsvGenerating] = useState(false);
  const [editingValues, setEditingValues] = useState(false);
  const [editedValues, setEditedValues] = useState([]);
  const [socioNome, setSocioNome] = useState("");
  const [socioCpf, setSocioCpf] = useState("");

  // Função para formatar a data
  const formatDate = (input) => {
    // Remove caracteres não numéricos
    const numbersOnly = input.replace(/\D/g, '');
    
    if (numbersOnly.length >= 6) {
      // Se o usuário digitou 6 dígitos ou mais, formatar como MM/AAAA
      const month = numbersOnly.substring(0, 2);
      const year = numbersOnly.substring(2, 6);
      return `${month}/${year}`;
    } else if (numbersOnly.length > 2) {
      // Se digitou mais de 2 dígitos, adicionar a barra após o mês
      const month = numbersOnly.substring(0, 2);
      const rest = numbersOnly.substring(2);
      return `${month}/${rest}`;
    }
    
    return numbersOnly;
  };

  // Função para calcular a data inicial com base na data final (12 meses antes)
  const calculateStartDate = (endDateValue) => {
    // Verificar se a data final está no formato correto
    if (/^\d{2}\/\d{4}$/.test(endDateValue)) {
      const [month, year] = endDateValue.split('/');
      let startMonth = parseInt(month) + 1;
      let startYear = parseInt(year) - 1;
      
      // Ajustar se o mês for maior que 12
      if (startMonth > 12) {
        startMonth = 1;
        startYear += 1;
      }
      
      // Formatar para ter dois dígitos no mês
      const formattedMonth = startMonth.toString().padStart(2, '0');
      return `${formattedMonth}/${startYear}`;
    }
    
    return "";
  };

  // Função para formatar valor monetário (sem side effects)
  const formatCurrency = (value) => {
    if (!value) return "";
    
    // Se já é uma string formatada, retorna como está
    if (typeof value === 'string' && value.includes('.') && value.includes(',')) {
      return value;
    }
    
    // Converte para número se necessário
    let numberValue;
    if (typeof value === 'string') {
      // Remove formatação existente e converte
      const cleanValue = value.replace(/\./g, '').replace(',', '.');
      numberValue = parseFloat(cleanValue);
    } else {
      numberValue = parseFloat(value);
    }
    
    // Se não for um número válido, retorna vazio
    if (isNaN(numberValue)) return "";
    
    // Formata o número com separador de milhares e duas casas decimais
    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter valor formatado para número
  const currencyToNumber = (formattedValue) => {
    if (!formattedValue) return 0;
    return parseFloat(formattedValue.replace(/\./g, '').replace(',', '.'));
  };

  // Handler para a mudança da data final
  const handleEndDateChange = (e) => {
    const formattedDate = formatDate(e.target.value);
    setEndDate(formattedDate);
    
    // Se a data estiver no formato correto, calcular a data inicial
    if (/^\d{2}\/\d{4}$/.test(formattedDate)) {
      const calculatedStartDate = calculateStartDate(formattedDate);
      setStartDate(calculatedStartDate);
    }
  };

  // Handler para a mudança da data inicial
  const handleStartDateChange = (e) => {
    setStartDate(formatDate(e.target.value));
  };

  // Handler para a mudança do valor de faturamento
  const handleRevenueChange = (e) => {
    const value = e.target.value;
    
    // Se estiver editando, aceitar mais caracteres para edição
    if (isEditing) {
      // Permitir apenas dígitos, pontos, vírgulas e remoções
      setRevenue(value);
    } else {
      // Modo inicial de entrada: permitir apenas números e uma vírgula
      if (/^[0-9]*,?[0-9]*$/.test(value) || value === "") {
        setRevenue(value);
      }
    }
  };

  // Handler para quando o campo de faturamento recebe foco
  const handleRevenueFocus = () => {
    setIsEditing(true);
  };

  // Handler para quando o campo de faturamento perde o foco
  const handleRevenueBlur = () => {
    setIsEditing(false);
    
    // Não formatar se o campo estiver vazio
    if (!revenue) return;
    
    // Limpando o valor para formatação
    let valueToFormat = revenue;
    
    // Se já contém formatação, faz a limpeza antes de reformatar
    if (revenue.includes('.')) {
      valueToFormat = revenue.replace(/\./g, '');
    }
    
    const formattedValue = formatCurrency(valueToFormat);
    setRevenue(formattedValue);
  };

  // Função para formatar CNPJ
  const formatCnpj = (value) => {
    if (!value) return "";
    
    // Verifica se o valor já foi formatado anteriormente
    if (value === lastFormattedCnpj) return value;
    
    // Remove caracteres não numéricos
    const numbersOnly = value.replace(/\D/g, '');
    
    // Limita a 14 dígitos
    const cnpjNumbers = numbersOnly.slice(0, 14);
    
    // Formata como XX.XXX.XXX/XXXX-XX
    if (cnpjNumbers.length <= 2) {
      return cnpjNumbers;
    } else if (cnpjNumbers.length <= 5) {
      return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2)}`;
    } else if (cnpjNumbers.length <= 8) {
      return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2, 5)}.${cnpjNumbers.slice(5)}`;
    } else if (cnpjNumbers.length <= 12) {
      return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2, 5)}.${cnpjNumbers.slice(5, 8)}/${cnpjNumbers.slice(8)}`;
    } else {
      return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2, 5)}.${cnpjNumbers.slice(5, 8)}/${cnpjNumbers.slice(8, 12)}-${cnpjNumbers.slice(12, 14)}`;
    }
  };

  // Função para formatar CPF
  const formatCpf = (value) => {
    if (!value) return "";
    const numbersOnly = value.replace(/\D/g, '');
    const cpfNumbers = numbersOnly.slice(0, 11);
    if (cpfNumbers.length <= 3) {
      return cpfNumbers;
    } else if (cpfNumbers.length <= 6) {
      return `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(3)}`;
    } else if (cpfNumbers.length <= 9) {
      return `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(3, 6)}.${cpfNumbers.slice(6)}`;
    } else {
      return `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(3, 6)}.${cpfNumbers.slice(6, 9)}-${cpfNumbers.slice(9, 11)}`;
    }
  };

  // Handler para a mudança do CPF do sócio
  const handleSocioCpfChange = (e) => {
    const formatted = formatCpf(e.target.value);
    setSocioCpf(formatted);
  };

  // Handler para a mudança do CNPJ
  const handleCnpjChange = (e) => {
    const value = e.target.value;
    
    // Se estiver editando, aceitar mais caracteres para edição
    if (isCnpjEditing) {
      setCnpj(value);
    } else {
      // Modo inicial de entrada: permitir apenas dígitos e formatação
      if (/^[\d.-/]*$/.test(value) || value === "") {
        setCnpj(value);
      }
    }
  };

  // Handler para quando o campo de CNPJ recebe foco
  const handleCnpjFocus = () => {
    setIsCnpjEditing(true);
  };

  // Handler para quando o campo de CNPJ perde o foco
  const handleCnpjBlur = () => {
    setIsCnpjEditing(false);
    
    // Não formatar se o campo estiver vazio
    if (!cnpj) return;
    
    const formattedValue = formatCnpj(cnpj);
    setCnpj(formattedValue);
    setLastFormattedCnpj(formattedValue);
  };

  // Função para buscar informações do CNPJ via proxy PHP (mesma solução do AgentRendPF)
  const fetchCnpjInfo = async (cnpjValue) => {
    try {
      // Limpa o CNPJ, deixando apenas números
      const cleanCnpj = cnpjValue.replace(/\D/g, '');
      
      console.log(`🔍 Consultando CNPJ ${cleanCnpj} via proxy PHP...`);
      
      // Usar o proxy PHP que utiliza cURL para fazer a requisição à ReceitaWS
      // Mesma solução implementada no AgentRendPF.jsx
      const response = await fetch('/api/cnpj_proxy.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cnpj: cleanCnpj
        })
      });
      
      if (!response.ok) {
        throw new Error(`Erro na consulta do CNPJ: HTTP ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Verificar se a resposta contém erro
      if (data.status === 'ERROR') {
        throw new Error(data.message || 'CNPJ não encontrado ou inválido');
      }
      
      console.log(`✅ CNPJ encontrado: ${data.nome}`);
      
      // Mapear os dados da ReceitaWS para o formato esperado pelo sistema
      return {
        cnpj: data.cnpj,
        nome_fantasia: data.fantasia || data.nome,
        empresa: {
          razao_social: data.nome
        },
        municipio: {
          descricao: data.municipio
        },
        uf: data.uf,
        situacao: data.situacao,
        porte: data.porte,
        natureza_juridica: data.natureza_juridica,
        atividade_principal: data.atividade_principal,
        atividades_secundarias: data.atividades_secundarias,
        qsa: data.qsa,
        logradouro: data.logradouro,
        numero: data.numero,
        complemento: data.complemento,
        bairro: data.bairro,
        cep: data.cep,
        email: data.email,
        telefone: data.telefone,
        data_situacao: data.data_situacao,
        capital_social: data.capital_social,
        abertura: data.abertura,
        ultima_atualizacao: data.ultima_atualizacao,
        simples: data.simples,
        simei: data.simei
      };
      
    } catch (err) {
      console.error('❌ Erro ao buscar informações do CNPJ:', err);
      throw err;
    }
  };

  // Função combinada para gerar e baixar o PDF com um único clique
  const generateAndDownloadPDF = async () => {
    console.log("Iniciando geração e download do PDF");
    setPdfGenerating(true);
    
    try {
      if (!result || !result.monthlyValues || result.monthlyValues.length === 0) {
        console.error("Não há dados para gerar o PDF");
        alert("Por favor, calcule a distribuição primeiro!");
        setPdfGenerating(false);
        return;
      }
      
      console.log("CNPJ formatado:", cnpj);
      console.log("Dados da empresa:", companyInfo);
      console.log("Dados para o PDF:", result);
      
      // Criar o PDF com a sintaxe correta
      // Não utilizaremos autoTable, vamos criar a tabela manualmente
      const doc = new jsPDF();
      console.log("Instância jsPDF criada");
      
      // Configurar fonte e tamanho
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      
      // Título centralizado
      doc.text('DECLARAÇÃO DE FATURAMENTO', doc.internal.pageSize.getWidth() / 2, 30, { align: 'center' });
      
      // Configurar fonte para o corpo do texto
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      
      // Texto da declaração
      const razaoSocial = companyInfo.empresa?.razao_social || '';
      const cnpjFormatado = formatCnpj(companyInfo.cnpj);
      
      // Layout alinhado à esquerda com as margens da tabela
      const tableStartX = 25; // Mesma posição inicial da tabela
      // const tableWidth = 160; // Largura total da tabela (80 + 80) - não utilizado
      // const tableEndX = tableStartX + tableWidth; // Posição final da tabela - não utilizado
      // const textAreaWidth = tableWidth; // Largura disponível para o texto - não utilizado
      
      const startYManual = 50;
      let currentY = startYManual;
      const lineHeight = 6;
      
      // LINHA 1: "Declaro para os devidos fins que a Empresa" (alinhado à esquerda)
      doc.setFont('helvetica', 'normal');
      doc.text("Declaro para os devidos fins que a Empresa", tableStartX, currentY);
      
      // LINHA 2: Nome da empresa (alinhado à esquerda, em negrito)
      currentY += lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.text(razaoSocial, tableStartX, currentY);
      
      // LINHA 3: "inscrito no CNPJ sob nº {CNPJ} teve como faturamento nos últimos 12" (alinhado à esquerda)
      currentY += lineHeight;
      doc.setFont('helvetica', 'normal');
      const linha3Inicio = "inscrito no CNPJ sob nº ";
      const linha3Fim = " teve como faturamento nos últimos 12";
      
      // Texto inicial
      doc.text(linha3Inicio, tableStartX, currentY);
      const linha3InicioWidth = doc.getTextWidth(linha3Inicio);
      
      // CNPJ em negrito
      doc.setFont('helvetica', 'bold');
      const cnpjWidth = doc.getTextWidth(cnpjFormatado);
      doc.text(cnpjFormatado, tableStartX + linha3InicioWidth, currentY);
      
      // Texto final da linha 3
      doc.setFont('helvetica', 'normal');
      doc.text(linha3Fim, tableStartX + linha3InicioWidth + cnpjWidth, currentY);
      
      // LINHA 4: "meses os valores discriminados abaixo:" (alinhado à esquerda)
      currentY += lineHeight;
      doc.text("meses os valores discriminados abaixo:", tableStartX, currentY)
      
      // Ajustar posição Y para tabela, considerando o espaço ocupado pelo texto introdutório
      let y = currentY + 20; // Reduzido de 15 para 20 (mais abaixo)
      
      // Tabela de faturamento manual - sem usar autoTable
      const tableLineHeight = 10;
      const colWidths = [80, 80]; // Larguras das colunas
      const startX = 25; // Posição inicial X
      
      // Cabeçalho da tabela
      doc.setFont('helvetica', 'bold');
      doc.text('MÊS', startX, y);
      doc.text('VALOR', startX + colWidths[0], y);
      
      // Linha sob o cabeçalho
      y += 2;
      doc.line(startX - 5, y, startX + colWidths[0] + colWidths[1] + 5, y);
      y += 5;
      
      // Corpo da tabela
      doc.setFont('helvetica', 'normal');
      
      // Adicionar os valores mensais
      result.monthlyValues.forEach((item, index) => {
        // Alternar cores de fundo (emulação)
        if (index % 2 === 0) {
          doc.setFillColor(245, 245, 245);
          doc.rect(startX - 5, y - 5, colWidths[0] + colWidths[1] + 10, tableLineHeight, 'F');
        }
        
        doc.text(item.month, startX, y);
        doc.text(`R$ ${item.value}`, startX + colWidths[0], y);
        y += tableLineHeight;
      });
      
      // Adicionar linha de total
      doc.setFont('helvetica', 'bold');
      y += 2;
      doc.line(startX - 5, y, startX + colWidths[0] + colWidths[1] + 5, y);
      y += 5;
      doc.text('TOTAL', startX, y);
      doc.text(`R$ ${result.totalSum}`, startX + colWidths[0], y);
      
      // Adicionar local e data na parte inferior
      let municipio = companyInfo.municipio?.descricao || '';
      const uf = companyInfo.uf || '';
      
      // Formatar o município para ter apenas as primeiras letras maiúsculas
      if (municipio) {
        municipio = municipio.toLowerCase().split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      }
      
      // Obter data atual por extenso
      const dataAtual = new Date();
      const dia = dataAtual.getDate();
      const meses = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];
      const mes = meses[dataAtual.getMonth()];
      const ano = dataAtual.getFullYear();
      
      const localData = `${municipio}/${uf}, ${dia} de ${mes} de ${ano}`;
      
      // Posicionar mais próximo da tabela conforme solicitado
      const finalY = y + 20; // Ajustado de 15 para 20 (um pouco mais abaixo)
      
      doc.text(localData, doc.internal.pageSize.getWidth() / 2, finalY, { align: 'center' });
      
      // Ajustando a posição do carimbo para a posição ideal (mais abaixo, conforme imagem)
      const carimboPosY = finalY + 24;
      
      // Configurar fonte Times tamanho 8 e itálico para o carimbo
      doc.setFont('times', 'italic');
      doc.setFontSize(8);
      
      const hasSocioData = socioNome.trim() !== '' && socioCpf.trim() !== '';
      
      if (hasSocioData) {
        // Layout lado a lado: sócio à esquerda, contador à direita
        const pageWidth = doc.internal.pageSize.getWidth();
        const leftX = pageWidth * 0.25;
        const rightX = pageWidth * 0.75;
        
        // Sócio (esquerda)
        doc.text(socioNome.trim().toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '), leftX, carimboPosY, { align: 'center' });
        doc.text(`CPF: ${formatCpf(socioCpf.trim())}`, leftX, carimboPosY + 5, { align: 'center' });
        doc.text('Sócio Administrador', leftX, carimboPosY + 10, { align: 'center' });
        
        // Contador (direita)
        doc.text('Francisco de Assis Rodrigues Fraga', rightX, carimboPosY, { align: 'center' });
        doc.text('CRC: RN-004432/O-0', rightX, carimboPosY + 5, { align: 'center' });
        doc.text('CPF: 188.177.854-15', rightX, carimboPosY + 10, { align: 'center' });
      } else {
        // Layout centralizado: apenas contador
        doc.text('Francisco de Assis Rodrigues Fraga', doc.internal.pageSize.getWidth() / 2, carimboPosY, { align: 'center' });
        doc.text('CRC: RN-004432/O-0', doc.internal.pageSize.getWidth() / 2, carimboPosY + 5, { align: 'center' });
        doc.text('Contador', doc.internal.pageSize.getWidth() / 2, carimboPosY + 10, { align: 'center' });
      }
      
      // Restaurar fonte padrão para o resto do documento
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      
      console.log("PDF criado com sucesso, iniciando download");
      
      // Baixar o PDF diretamente
      try {
        // Extrair o nome fantasia e formatar a data para o nome do arquivo
        const nomeFantasia = companyInfo.nome_fantasia || '';
        // Pegar o último mês dos valores (que é a data final)
        const dataFinal = result.monthlyValues[result.monthlyValues.length - 1].month.replace('/', '');
        
        // Formatar o nome do arquivo - só inclui o nome fantasia se existir
        const nomePDF = nomeFantasia 
          ? `FATURAMENTO ${nomeFantasia} ${dataFinal}` 
          : `FATURAMENTO ${dataFinal}`;
          
        doc.save(nomePDF + '.pdf');
        console.log("Download iniciado com sucesso");
      } catch (error) {
        console.error("Erro ao baixar o PDF:", error);
        throw error; // Propagar o erro para ser capturado pelo try/catch externo
      }
      
      // Concluir o processo
      setPdfGenerating(false);
      return true;
    } catch (err) {
      console.error('Erro ao gerar ou baixar PDF:', err);
      console.error(err.stack);
      alert("Ocorreu um erro: " + err.message);
      setPdfGenerating(false);
      return false;
    }
  };

  // Função para gerar e baixar o arquivo CSV com um único clique
  const generateAndDownloadCSV = async () => {
    console.log("Iniciando geração e download do CSV");
    setCsvGenerating(true);
    
    try {
      if (!result || !result.monthlyValues || result.monthlyValues.length === 0) {
        console.error("Não há dados para gerar o CSV");
        alert("Por favor, calcule a distribuição primeiro!");
        setCsvGenerating(false);
        return;
      }
      
      console.log("Dados para o CSV:", result);
      
      // Criar o conteúdo do CSV
      let csvContent = "";
      
      // Adicionar cada linha no formato MMAAAA,VALOR
      result.monthlyValues.forEach((item) => {
        // Extrair mês e ano do formato MM/AAAA
        const [month, year] = item.month.split('/');
        
        // Remover o "R$" e formatação (pontos e vírgulas) do valor
        // Substituir vírgula por ponto para formato numérico internacional
        const valueWithDot = item.value.replace(/\./g, '').replace(',', '.');
        
        // Montar a linha no formato solicitado: MMAAAA,VALOR
        const csvLine = `${month}${year},${valueWithDot}\n`;
        csvContent += csvLine;
      });
      
      // Criar o blob com o conteúdo do CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      // Criar link para download
      const link = document.createElement('a');
      
      // Extrair o nome fantasia e formatar a data para o nome do arquivo
      const nomeFantasia = companyInfo.nome_fantasia || '';
      // Pegar o último mês dos valores (que é a data final)
      const dataFinal = result.monthlyValues[result.monthlyValues.length - 1].month.replace('/', '');
      
      // Formatar o nome do arquivo - só inclui o nome fantasia se existir
      const nomeCSV = nomeFantasia 
        ? `FATURAMENTO ${nomeFantasia} ${dataFinal}` 
        : `FATURAMENTO ${dataFinal}`;
      
      link.href = url;
      link.setAttribute('download', nomeCSV + '.csv');
      
      // Simular clique no link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL criada
      URL.revokeObjectURL(url);
      
      console.log("CSV gerado e baixado com sucesso");
      setCsvGenerating(false);
      return true;
    } catch (err) {
      console.error('Erro ao gerar ou baixar CSV:', err);
      console.error(err.stack);
      alert("Ocorreu um erro: " + err.message);
      setCsvGenerating(false);
      return false;
    }
  };

  // Função para gerar percentual adicional aleatório entre 1,86584% e 2,48595%
  const generateRandomBonus = () => {
    const min = 1.86584;
    const max = 2.48595;
    return Math.random() * (max - min) + min;
  };

  // Função para gerar pesos mensais baseados em padrões de negócio
  const getMonthlyWeights = () => {
    return [
      1.2,  // Janeiro (ajuste pós-feriados)
      0.8,  // Fevereiro (tipicamente mais lento)
      1.1,  // Março (fim do ano fiscal)
      0.9,  // Abril
      1.0,  // Maio
      1.1,  // Junho (meio do ano)
      0.9,  // Julho
      0.8,  // Agosto (temporada de férias)
      1.2,  // Setembro (volta aos negócios)
      1.1,  // Outubro
      1.0,  // Novembro
      1.3   // Dezembro (temporada de feriados)
    ];
  };

  // Função para gerar percentuais aleatórios que somam 100%
  const generateRandomPercentages = (count = 12, minPercent = 6.0, maxPercent = 9.0) => {
    console.log("🎲 Gerando percentuais aleatórios...");
    const percentages = new Array(count);
    
    // Gerar percentuais iniciais balanceados
    for (let i = 0; i < count; i++) {
      percentages[i] = 100 / count;
    }
    
    // Aplicar perturbação controlada
    for (let iteration = 0; iteration < 500; iteration++) {
      const newPercentages = percentages.map(p => {
        const noise = (Math.random() - 0.5) * 0.6; // Ruído controlado
        return p + noise;
      });
      
      // Normalizar para somar 100%
      const sum = newPercentages.reduce((acc, val) => acc + val, 0);
      const normalized = newPercentages.map(p => (p / sum) * 100);
      
      // Verificar se está dentro dos limites
      const withinLimits = normalized.every(p => p >= minPercent && p <= maxPercent && p <= 15);
      
      if (withinLimits) {
        // Verificar se meses consecutivos não são muito similares
        let validDistribution = true;
        for (let i = 0; i < normalized.length - 1; i++) {
          if (Math.abs(normalized[i] - normalized[i + 1]) < 0.5) {
            validDistribution = false;
            break;
          }
        }
        
        if (validDistribution) {
          console.log("✅ Percentuais gerados com sucesso:", normalized.map(p => p.toFixed(2) + "%"));
          return normalized;
        }
      }
      
      // Ajustar gradualmente
      for (let i = 0; i < count; i++) {
        percentages[i] = Math.max(minPercent, Math.min(maxPercent, normalized[i]));
      }
      
      // Renormalizar
      const adjustedSum = percentages.reduce((acc, val) => acc + val, 0);
      for (let i = 0; i < count; i++) {
        percentages[i] = (percentages[i] / adjustedSum) * 100;
      }
    }
    
    console.log("⚠️ Usando distribuição de fallback");
    return percentages;
  };

  // Função para distribuir receita localmente
  const distributeRevenue = (totalRevenue) => {
    console.log("🔄 Iniciando distribuição de receita local...");
    console.log("💰 Valor total a distribuir:", totalRevenue);
    
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    
    // Obter pesos mensais
    const weights = getMonthlyWeights();
    console.log("⚖️ Pesos mensais:", weights);
    
    // Gerar percentuais aleatórios
    const percentages = generateRandomPercentages(12);
    
    // Aplicar pesos com influência controlada
    const weightedPercentages = percentages.map((p, i) => p * weights[i]);
    const weightSum = weightedPercentages.reduce((acc, val) => acc + val, 0);
    const normalizedPercentages = weightedPercentages.map(p => (p / weightSum) * 100);
    
    console.log("📊 Percentuais finais por mês:", normalizedPercentages.map(p => p.toFixed(2) + "%"));
    
    // Calcular valores mensais iniciais
    const monthlyValues = {};
    let runningTotal = 0;
    
    // Calcular valores para os primeiros 11 meses
    for (let i = 0; i < 11; i++) {
      const value = Math.round((totalRevenue * normalizedPercentages[i] / 100) * 100) / 100;
      monthlyValues[months[i]] = value;
      runningTotal += value;
    }
    
    // Ajustar o último mês para garantir que a soma seja exata
    monthlyValues[months[11]] = Math.round((totalRevenue - runningTotal) * 100) / 100;
    
    console.log("📅 Valores mensais antes do bônus:", monthlyValues);
    
    // Gerar percentual de bônus aleatório
    const bonusPercentage = generateRandomBonus();
    const bonusAmount = Math.round((totalRevenue * bonusPercentage / 100) * 100) / 100;
    
    console.log("🎁 Percentual de bônus:", bonusPercentage.toFixed(5) + "%");
    console.log("💎 Valor do bônus:", bonusAmount);
    
    // Distribuir o bônus proporcionalmente entre os meses
    const totalBeforeBonus = Object.values(monthlyValues).reduce((sum, value) => sum + value, 0);
    const finalMonthlyValues = {};
    let finalRunningTotal = 0;
    
    for (let i = 0; i < 11; i++) {
      const proportion = monthlyValues[months[i]] / totalBeforeBonus;
      const monthBonus = Math.round((bonusAmount * proportion) * 100) / 100;
      finalMonthlyValues[months[i]] = monthlyValues[months[i]] + monthBonus;
      finalRunningTotal += finalMonthlyValues[months[i]];
    }
    
    // Ajustar o último mês para garantir que a soma final seja exata
    const expectedTotal = totalRevenue + bonusAmount;
    finalMonthlyValues[months[11]] = Math.round((expectedTotal - finalRunningTotal) * 100) / 100;
    
    const finalTotalSum = Object.values(finalMonthlyValues).reduce((sum, value) => sum + value, 0);
    
    console.log("🏆 Valores mensais finais (com bônus):", finalMonthlyValues);
    console.log("🎯 Total final:", finalTotalSum);
    console.log("✅ Diferença do valor original:", (finalTotalSum - totalRevenue).toFixed(2));
    
    return {
      monthlyValues: finalMonthlyValues,
      totalSum: finalTotalSum,
      originalTotal: totalRevenue,
      bonusPercentage: bonusPercentage,
      bonusAmount: bonusAmount
    };
  };

  // Handler para o envio do formulário
  const handleSubmit = async () => {
    console.log("🚀 INICIANDO PROCESSAMENTO LOCAL");
    
    try {
      // Validar datas
      if (!/^\d{2}\/\d{4}$/.test(startDate) || !/^\d{2}\/\d{4}$/.test(endDate)) {
        setError("Por favor, insira datas válidas no formato MM/AAAA.");
        return;
      }
      
      // Validar valor
      const numericRevenue = currencyToNumber(revenue);
      console.log("💰 Valor de entrada:", numericRevenue);
      
      if (isNaN(numericRevenue) || numericRevenue <= 0) {
        setError("Por favor, insira um valor de faturamento válido.");
        return;
      }
      
      // Validar CNPJ
      if (!cnpj || cnpj.replace(/\D/g, '').length !== 14) {
        setError("Por favor, insira um CNPJ válido.");
        return;
      }
      
      setProcessing(true);
      setError(null);
      setCompanyInfo(null);
      
      // Buscar informações do CNPJ
      try {
        console.log("🔍 Buscando informações do CNPJ...");
        const cnpjInfo = await fetchCnpjInfo(cnpj);
        setCompanyInfo(cnpjInfo);
        console.log("✅ CNPJ encontrado:", cnpjInfo.empresa?.razao_social);
      } catch (err) {
        setError(`Erro ao consultar o CNPJ: ${err.message}`);
        setProcessing(false);
        return;
      }
      
      // DISTRIBUIÇÃO LOCAL (SEM API EXTERNA)
      console.log("🎯 Iniciando distribuição local...");
      const distributionResult = distributeRevenue(numericRevenue);
      
      console.log("📊 Resultado da distribuição:", distributionResult);
      console.log("💎 Percentual de bônus aplicado:", distributionResult.bonusPercentage.toFixed(5) + "%");
      console.log("💰 Valor original:", distributionResult.originalTotal);
      console.log("🎁 Valor do bônus:", distributionResult.bonusAmount);
      console.log("🏆 Total final:", distributionResult.totalSum);
      
      // Mapear os valores mensais para o formato esperado pela interface
      const [startMonth, startYear] = startDate.split('/').map(num => parseInt(num));
      const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                         'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      
      const monthlyValues = [];
      let currentMonth = startMonth;
      let currentYear = startYear;
      
      for (let i = 0; i < 12; i++) {
        // Calcular o índice do mês corretamente
        const monthIndex = (currentMonth - 1) % 12;
        const monthName = monthNames[monthIndex];
        const value = distributionResult.monthlyValues[monthName];
        
        monthlyValues.push({
          month: `${currentMonth.toString().padStart(2, '0')}/${currentYear}`,
          value: value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        });
        
        console.log(`📅 ${monthName} (${currentMonth.toString().padStart(2, '0')}/${currentYear}): R$ ${value.toFixed(2)}`);
        
        // Avançar para o próximo mês
        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
      }
      
      setResult({
        monthlyValues,
        totalSum: distributionResult.totalSum.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        originalTotal: distributionResult.originalTotal,
        bonusPercentage: distributionResult.bonusPercentage,
        bonusAmount: distributionResult.bonusAmount
      });
      
      console.log("✅ PROCESSAMENTO CONCLUÍDO COM SUCESSO!");
      
    } catch (err) {
      console.error("❌ ERRO NO PROCESSAMENTO:", err);
      setError(`Erro ao processar: ${err.message}`);
      setCompanyInfo(null);
    } finally {
      setProcessing(false);
    }
  };

  // Função para copiar valor para a área de transferência
  const copyToClipboard = async (value, index) => {
    try {
      const textToCopy = value.replace("R$ ", "").trim();
      await navigator.clipboard.writeText(textToCopy);
      setCopiedIndices(prevIndices => new Set(prevIndices).add(index));
      setLastCopiedIndexForAnimation(index); // For the "Copiado!" text animation
      setTimeout(() => {
        setLastCopiedIndexForAnimation(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      // Handle error (e.g., show a toast message)
    }
  };

  // Função para iniciar edição dos valores
  const startEditingValues = () => {
    setEditingValues(true);
    // Copiar valores atuais para edição
    const currentValues = result.monthlyValues.map(item => ({
      month: item.month,
      value: item.value.replace(/\./g, '').replace(',', '.') // Converter para formato numérico
    }));
    setEditedValues(currentValues);
  };

  // Função para cancelar edição
  const cancelEditingValues = () => {
    setEditingValues(false);
    setEditedValues([]);
  };

  // Função para salvar valores editados e reprocessar
  const saveEditedValues = () => {
    console.log("💾 Salvando valores editados e reprocessando...");
    
    try {
      // Calcular novo total
      const newTotal = editedValues.reduce((sum, item) => sum + parseFloat(item.value), 0);
      console.log("📊 Novo total calculado:", newTotal.toFixed(2));
      
      // Formatar valores para exibição
      const formattedValues = editedValues.map(item => ({
        month: item.month,
        value: parseFloat(item.value).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }));
      
      // Atualizar resultado com novos valores
      const newResult = {
        ...result,
        monthlyValues: formattedValues,
        totalSum: newTotal.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        isEdited: true // Flag para indicar que foi editado manualmente
      };
      
      setResult(newResult);
      setEditingValues(false);
      setEditedValues([]);
      
      console.log("✅ Valores atualizados com sucesso");
    } catch (error) {
      console.error("❌ Erro ao salvar valores editados:", error);
      alert("Erro ao salvar valores. Verifique se todos os valores são números válidos.");
    }
  };

  // Função para atualizar um valor editado
  const updateEditedValue = (index, newValue) => {
    const updatedValues = [...editedValues];
    updatedValues[index].value = newValue;
    setEditedValues(updatedValues);
  };

  return (
    <div className="container mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Link to={createPageUrl("Operations")}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Distribuição de Faturamento Anual</h1>
          </div>
        </div>
        <p className="text-gray-500">
          Distribua seu faturamento anual em 12 meses para auxiliar no planejamento financeiro, previsões de fluxo de caixa e orçamento.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Calculo de Distribuição</CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm mb-6">
                  <p className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </p>
                </div>
              )}

              {!result ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start-date" className="block mb-2">
                        Data Inicial (MM/AAAA)
                      </Label>
                      <div className="relative">
                        <CalendarRange className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                        <Input
                          id="start-date"
                          className="pl-10"
                          placeholder="MM/AAAA"
                          value={startDate}
                          onChange={handleStartDateChange}
                          disabled={processing}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Calculada automaticamente (12 meses antes da data final)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="end-date" className="block mb-2">
                        Data Final (MM/AAAA)
                      </Label>
                      <div className="relative">
                        <CalendarRange className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                        <Input
                          id="end-date"
                          className="pl-10"
                          placeholder="MM/AAAA"
                          value={endDate}
                          onChange={handleEndDateChange}
                          disabled={processing}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Data final do período de 12 meses
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="revenue" className="block mb-2">
                      Faturamento Anual
                    </Label>
                    <div className="relative">
                      <DollarSign className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="revenue"
                        className="pl-10"
                        placeholder="0,00"
                        value={revenue}
                        onChange={handleRevenueChange}
                        onFocus={handleRevenueFocus}
                        onBlur={handleRevenueBlur}
                        disabled={processing}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="cnpj" className="block mb-2">
                      CNPJ da Empresa
                    </Label>
                    <div className="relative">
                      <FileText className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                      <Input
                        id="cnpj"
                        className="pl-10"
                        placeholder="00.000.000/0000-00"
                        value={cnpj}
                        onChange={handleCnpjChange}
                        onFocus={handleCnpjFocus}
                        onBlur={handleCnpjBlur}
                        disabled={processing}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      CNPJ necessário para geração da declaração em PDF
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="socio-nome" className="block mb-2">
                        Nome do Sócio (opcional)
                      </Label>
                      <div className="relative">
                        <FileText className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                        <Input
                          id="socio-nome"
                          className="pl-10"
                          placeholder="Nome do sócio"
                          value={socioNome}
                          onChange={(e) => setSocioNome(e.target.value)}
                          disabled={processing}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="socio-cpf" className="block mb-2">
                        CPF do Sócio (opcional)
                      </Label>
                      <div className="relative">
                        <FileText className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                        <Input
                          id="socio-cpf"
                          className="pl-10"
                          placeholder="000.000.000-00"
                          value={socioCpf}
                          onChange={handleSocioCpfChange}
                          disabled={processing}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Se preenchido, os dados do sócio aparecerão na assinatura do PDF
                  </p>

                  {processing ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
                        <h3 className="text-lg font-medium text-blue-800 mb-2">Processando...</h3>
                        <p className="text-blue-700 mb-4">
                          Calculando a distribuição do faturamento
                        </p>
                        <div className="w-full max-w-xs mb-2">
                          <Progress value={80} className="h-2" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      onClick={handleSubmit} 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={!startDate || !endDate || !revenue || !cnpj || processing}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Calcular Distribuição
                    </Button>
                  )}
                </div>
              ) : (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm mb-6">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      <span>Distribuição calculada com sucesso! Valor total: {result.totalSum}</span>
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">Valores Mensais</h3>
                    {!editingValues && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={startEditingValues}
                        className="flex items-center"
                      >
                        <Edit3 className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                    )}
                  </div>
                  
                  {editingValues ? (
                    // Modo de edição
                    <div className="space-y-3">
                      {editedValues.map((item, index) => (
                        <div key={index} className="flex items-stretch h-14 gap-3">
                          <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200 flex items-center justify-center font-medium text-gray-700 min-w-[150px] w-[150px]">
                            {item.month}
                          </div>
                          <div className="flex-1 bg-white rounded-lg px-4 py-2 border border-gray-200 flex items-center justify-between">
                            <span className="text-sm text-gray-500 mr-2">R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.value}
                              onChange={(e) => updateEditedValue(index, e.target.value)}
                              className="flex-1 text-right font-mono text-lg font-semibold"
                            />
                          </div>
                        </div>
                      ))}
                      
                      {/* Botões de edição */}
                      <div className="flex justify-end space-x-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditingValues}
                          className="flex items-center"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveEditedValues}
                          className="flex items-center bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Save className="h-4 w-4 mr-1" />
                          Salvar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Modo de visualização
                    <div className="space-y-3">
                    {result.monthlyValues.map((item, index) => (
                      <div key={index} className="relative group">
                        <div className="flex items-stretch h-14 gap-3">
                          <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200 flex items-center justify-center font-medium text-gray-700 min-w-[150px] w-[150px]">
                            {item.month}
                          </div>
                          <div className="flex-1 bg-gray-50 rounded-lg px-4 py-2 border border-gray-200 hover:border-blue-300 transition-colors flex items-center justify-between">
                            <span className="text-lg font-semibold">R$ {item.value}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(`R$ ${item.value}`, index)}
                              className="opacity-80 hover:opacity-100"
                            >
                              {copiedIndices.has(index) ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        {lastCopiedIndexForAnimation === index && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute top-0 right-12 bg-black text-white text-xs py-1 px-2 rounded"
                          >
                            Copiado!
                          </motion.div>
                        )}
                      </div>
                    ))}
                    </div>
                  )}
                  
                  <div className="mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setResult(null);
                        setCopiedIndices(new Set());
                        setLastCopiedIndexForAnimation(null);
                      }}
                    >
                      Calcular Novamente
                    </Button>
                  </div>
                  
                  <div className="flex justify-between mb-6">
                    <h3 className="font-semibold text-lg">Valores Mensais</h3>
                    
                    {companyInfo && (
                      <div className="flex gap-2">
                        <Button 
                          onClick={generateAndDownloadPDF} 
                          className="w-fit bg-green-600 hover:bg-green-700 text-white"
                          disabled={pdfGenerating}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Baixar Declaração
                        </Button>
                        <Button 
                          onClick={generateAndDownloadCSV} 
                          className="w-fit bg-blue-600 hover:bg-blue-700 text-white"
                          disabled={csvGenerating}
                        >
                          <FileSpreadsheet className="mr-2 h-4 w-4" />
                          Baixar CSV
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Informações do bônus aplicado */}
                  {result.bonusPercentage && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm mb-6">
                      <p>
                        <strong>Valor Original:</strong> {result.originalTotal.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </p>
                      <p>
                        <strong>Percentual de Bônus:</strong> {result.bonusPercentage.toFixed(5)}%
                      </p>
                      <p>
                        <strong>Valor do Bônus:</strong> {result.bonusAmount.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </p>
                      <p>
                        <strong>Total Final:</strong> {result.totalSum}
                      </p>
                    </div>
                  )}

                  {companyInfo && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm mb-6">
                      <p>
                        <strong>Empresa:</strong> {companyInfo.empresa?.razao_social}
                      </p>
                      <p>
                        <strong>CNPJ:</strong> {formatCnpj(companyInfo.cnpj)}
                      </p>
                      <p>
                        <strong>Município/UF:</strong> {companyInfo.municipio?.descricao}/{companyInfo.uf}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-1">O que é isso?</h3>
                  <p className="text-sm text-gray-600">
                    Esta ferramenta distribui seu faturamento anual em 12 meses para auxiliar no planejamento financeiro, previsões de fluxo de caixa e orçamento.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-medium mb-1">Como usar</h3>
                  <ol className="text-sm text-gray-600 space-y-2 list-decimal pl-4">
                    <li>Insira a data inicial do período (MM/AAAA)</li>
                    <li>A data final será calculada automaticamente (12 meses depois)</li>
                    <li>Informe o valor total do faturamento anual</li>
                    <li>Insira o CNPJ da empresa</li>
                    <li>Clique em "Calcular Distribuição"</li>
                    <li>Copie os valores mensais conforme necessário</li>
                    <li>Gere a declaração de faturamento em PDF</li>
                    <li>Baixe os valores em formato CSV se necessário</li>
                  </ol>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800 text-sm">
                  <p>
                    <strong>Dica:</strong> Você pode ajustar a data final manualmente se desejar um período diferente de 12 meses.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
